import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { addDays, alignBack, successRate, streaks, weekdayPattern, type LocalDate, type UEResult } from '@habitos/engine';
import { useI18n } from '../i18n';
import { focusStats } from '../domain/focus';
import { useApp } from '../state';
import { evaluate, liveHabits } from '../domain/selectors';
import { currentPlan } from '../domain/actions';
import { currentWeekStartOf, toEngineSettings } from '../domain/model';
import { HabitIcon } from '../ui/HabitIcon';
import { Empty } from '../ui/Empty';
import { streakText } from '../ui/format';
import { habitRates } from '../domain/progress';
import { MIN_EVALUABLE, NEGLECT_THRESHOLD } from '../assistant/insights';

export function StatsScreen() {
  const { t, fmtDate } = useI18n();
  const { data, ctx, idx } = useApp();
  const [premium, setPremium] = useState(false);
  useEffect(() => {
    void currentPlan().then((p) => setPremium(p === 'premium'));
  }, []);
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const habits = useMemo(() => liveHabits(data.habits).filter((h) => h.kind !== 'task' && !h.archivedAt), [data.habits]);
  // El mapa de días cumplidos usa la misma regla que el progreso de Hoy (FR-HAB-14).
  const counted = useMemo(() => new Set(habits.filter((h) => !h.excludedFromProgress).map((h) => h.id)), [habits]);
  const from30 = addDays(ctx.today, -29);

  const perHabit = useMemo(
    () =>
      habits.map((h) => {
        const results = evaluate(h, idx, data.vacations, settings, ctx.today);
        return { habit: h, results, rate30: successRate(results, { from: from30, to: ctx.today }), streak: streaks(results) };
      }),
    [habits, idx, data.vacations, settings, ctx.today, from30],
  );

  const all: UEResult[] = perHabit.flatMap((p) => p.results);
  const countedResults: UEResult[] = perHabit.filter((p) => counted.has(p.habit.id)).flatMap((p) => p.results);
  const overall = successRate(all, { from: from30, to: ctx.today });
  const bestCurrent = perHabit.reduce((acc, p) => (p.streak.current > acc.n ? { n: p.streak.current, unit: p.streak.unit } : acc), { n: 0, unit: 'day' as 'day' | 'week' | 'month' });

  // Últimas 12 semanas: porcentaje de UE diarias cumplidas por semana.
  const ws = currentWeekStartOf(ctx.settings);
  const weeks: { start: LocalDate; rate: number | null }[] = [];
  const thisWeek = alignBack(ctx.today, ws);
  for (let i = 11; i >= 0; i--) {
    const start = addDays(thisWeek, -7 * i);
    weeks.push({ start, rate: successRate(all, { from: start, to: addDays(start, 6) }).rate });
  }

  // Mapa de calor de los últimos 140 días: fracción de hábitos cumplidos cada día.
  const heatStart = alignBack(addDays(ctx.today, -139), ws);
  const heatDays: { d: LocalDate; level: number; label: string }[] = [];
  for (let d = heatStart; d <= ctx.today; d = addDays(d, 1)) {
    const day = countedResults.filter((r) => r.kind === 'day' && r.start === d && ['completed', 'missed', 'failed'].includes(r.status));
    const ratio = day.length ? day.filter((r) => r.status === 'completed').length / day.length : 0;
    const level = day.length === 0 ? 0 : Math.max(1, Math.ceil(ratio * 4));
    heatDays.push({ d, level: ratio === 0 && day.length > 0 ? 0 : level, label: `${fmtDate(d)}: ${Math.round(ratio * 100)} %` });
  }

  if (habits.length === 0) {
    return (
      <div className="stack-l">
        <h1>{t('stats.title')}</h1>
        <Empty title={t('stats.title')} body={t('stats.empty')} />
        <FocusStatsCard />
      </div>
    );
  }

  const pct = (r: number | null) => (r === null ? t('common.dash') : `${Math.round(r * 100)} %`);

  return (
    <div className="stack-l">
      <h1>{t('stats.title')}</h1>
      <GoalsCard />
      <Link to="/review" className="btn btn--outline">
        {t('review.open')}
      </Link>
      <section className="stack" aria-label={t('stats.overall')}>
        <h2 className="section-title">{t('stats.overall')}</h2>
        <div className="stat-grid">
          <div className="stat">
            <div className="stat__value">{pct(overall.rate)}</div>
            <div className="stat__label">{t('detail.successRate')}</div>
          </div>
          <div className="stat">
            <div className="stat__value num">{overall.completed}</div>
            <div className="stat__label">{t('stats.completedPeriods')}</div>
          </div>
          <div className="stat">
            <div className="stat__value num">{habits.length}</div>
            <div className="stat__label">{t('stats.activeHabits')}</div>
          </div>
          {!ctx.settings.hideStreaks && (
            <div className="stat">
              <div className="stat__value">{streakText(t, bestCurrent.n, bestCurrent.unit)}</div>
              <div className="stat__label">{t('stats.bestCurrent')}</div>
            </div>
          )}
        </div>
      </section>

      <section className="card stack" aria-labelledby="heat-title">
        <h2 id="heat-title">{t('stats.heatTitle')}</h2>
        <p className="muted small">{t('stats.heatHelp')}</p>
        <div className="heat" role="img" aria-label={t('stats.heatTitle')}>
          {heatDays.map((h) => (
            <span key={h.d} className="heat__cell" data-l={h.level} title={h.label} />
          ))}
        </div>
      </section>

      <WeekdayCard results={all} />

      <FocusStatsCard />

      <section className="card stack" aria-label={t('stats.weekly')}>
        <h2>{t('stats.weekly')}</h2>
        {premium ? (
          <>
            <div className="bars" role="list">
              {weeks.map((w) => (
                <div
                  key={w.start}
                  role="listitem"
                  className="bars__bar"
                  data-empty={w.rate === null ? 'true' : undefined}
                  style={{ height: `${Math.max(2, (w.rate ?? 0) * 100)}%` }}
                  aria-label={`${fmtDate(w.start)}: ${pct(w.rate)}`}
                  title={`${fmtDate(w.start)}: ${pct(w.rate)}`}
                />
              ))}
            </div>
          </>
        ) : (
          <p className="muted small">
            {t('stats.premiumHint')} <Link to="/settings/plan">{t('plan.title')}</Link>
          </p>
        )}
      </section>

      <section className="stack" aria-label={t('stats.perHabit')}>
        <h2 className="section-title">{t('stats.perHabit')}</h2>
        <div className="list-card">
          {perHabit.map((p) => (
            <Link key={p.habit.id} to={`/habits/${p.habit.id}`} className="list-item" data-color={p.habit.color}>
              <div className="habit__icon">
                <HabitIcon name={p.habit.icon} />
              </div>
              <div className="list-item__body">
                <div className="list-item__title">{p.habit.name}</div>
                <div className="list-item__sub">
                  {t('detail.last30')}: {pct(p.rate30.rate)}
                  {!ctx.settings.hideStreaks && !p.habit.hideStreak ? ` · ${streakText(t, p.streak.current, p.streak.unit)}` : ''}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

/**
 * Patrón por día de la semana: en qué días de la semana cumples más y en cuáles menos.
 * Responde a "¿por qué se me caen los hábitos?" sin pedir ningún dato nuevo al usuario.
 */
function WeekdayCard({ results }: { results: UEResult[] }) {
  const { t, m } = useI18n();
  const { ctx } = useApp();
  const from = addDays(ctx.today, -89);
  const rows = useMemo(() => weekdayPattern(results, { from, to: ctx.today }), [results, from, ctx.today]);
  const rated = rows.filter((r) => r.rate !== null);
  if (rated.length === 0) return null;

  const pct = (r: number | null) => (r === null ? t('common.dash') : `${Math.round(r * 100)} %`);
  const total = rated.reduce((acc, r) => acc + r.total, 0);
  // Con muy pocos días cerrados cualquier diferencia es ruido: se muestran las barras sin conclusión.
  const enough = total >= 14 && rated.length >= 3;
  const best = rated.reduce((a, b) => (b.rate! > a.rate! ? b : a));
  const worst = rated.reduce((a, b) => (b.rate! < a.rate! ? b : a));
  const flat = best.rate! - worst.rate! < 0.15;
  const name = (weekday: number) => m.weekdays.long[weekday - 1] ?? String(weekday);

  return (
    <section className="card stack" aria-labelledby="weekday-title">
      <h2 id="weekday-title">{t('stats.weekday.title')}</h2>
      <p className="muted small">{t('stats.weekday.help')}</p>
      <div className="bars" role="list">
        {rows.map((r) => (
          <div
            key={r.weekday}
            role="listitem"
            className="bars__bar"
            data-empty={r.rate === null ? 'true' : undefined}
            style={{ height: `${Math.max(2, (r.rate ?? 0) * 100)}%` }}
            aria-label={t('stats.weekday.cell', { day: name(r.weekday), rate: pct(r.rate), done: r.completed, total: r.total })}
            title={t('stats.weekday.cell', { day: name(r.weekday), rate: pct(r.rate), done: r.completed, total: r.total })}
          />
        ))}
      </div>
      <div className="weekday-axis" aria-hidden="true">
        {rows.map((r) => (
          <span key={r.weekday}>{m.weekdays.short[r.weekday - 1]}</span>
        ))}
      </div>
      <p className="small">
        {!enough
          ? t('stats.weekday.few')
          : flat
            ? t('stats.weekday.flat')
            : `${t('stats.weekday.best', { day: name(best.weekday), rate: pct(best.rate) })} ${t('stats.weekday.worst', { day: name(worst.weekday), rate: pct(worst.rate) })}`}
      </p>
    </section>
  );
}

/** Resumen de enfoque: visible aunque no haya hábitos (el temporizador no los exige). */
function FocusStatsCard() {
  const { t } = useI18n();
  const { data, ctx } = useApp();
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const focus = useMemo(() => focusStats(data.focus, ctx.today, settings), [data.focus, ctx.today, settings]);
  return (
    <section className="card stack" aria-labelledby="focus-stats-title">
      <h2 id="focus-stats-title">{t('focus.statsTitle')}</h2>
      <p className="num">{t('focus.statsSummary', { today: focus.todayMinutes, week: focus.weekMinutes })}</p>
      <p className="muted small">{t('focus.statsSessions', { count: focus.sessionsWeek })}</p>
      {focus.byHabit.length > 0 && (
        <ul className="small" style={{ margin: 0, paddingLeft: '1.2em' }}>
          {focus.byHabit.slice(0, 5).map((b) => (
            <li key={b.habitId ?? 'none'}>
              {(b.habitId && data.habits.find((h) => h.id === b.habitId)?.name) || t('focus.statsUnlinked')}: <span className="num">{b.minutes} min</span>
            </li>
          ))}
        </ul>
      )}
      <Link to="/focus" className="small">
        {t('focus.open')}
      </Link>
    </section>
  );
}

/**
 * Objetivos: constancia de cada hábito en los últimos 14 días cerrados, con la misma regla
 * que usa el asistente para "¿qué hábitos estoy descuidando?".
 */
function GoalsCard() {
  const { t, fmtDate } = useI18n();
  const { data, ctx, idx } = useApp();
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const to = addDays(ctx.today, -1);
  const from = addDays(ctx.today, -14);
  const rows = useMemo(
    () => habitRates(data.habits, idx, data.vacations, settings, ctx.today, from, to).sort((a, b) => (a.rate ?? 2) - (b.rate ?? 2)),
    [data.habits, idx, data.vacations, settings, ctx.today, from, to],
  );
  if (!rows.length) return null;
  const atRisk = rows.filter((r) => r.total >= MIN_EVALUABLE && (r.rate ?? 0) < NEGLECT_THRESHOLD).length;
  return (
    <section className="card stack goals" aria-labelledby="goals-title">
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <h2 id="goals-title">{t('stats.goals.title')}</h2>
        <span className="muted xsmall">{t('stats.goals.period', { from: fmtDate(from, { day: 'numeric', month: 'short' }), to: fmtDate(to, { day: 'numeric', month: 'short' }) })}</span>
      </div>
      <p className="small">{atRisk ? t('stats.goals.atRisk', { count: atRisk, threshold: Math.round(NEGLECT_THRESHOLD * 100) }) : t('stats.goals.allGood')}</p>
      <ul className="goals__list">
        {rows.map((r) => {
          const few = r.total < MIN_EVALUABLE;
          const risk = !few && (r.rate ?? 0) < NEGLECT_THRESHOLD;
          return (
            <li key={r.habit.id} className="goals__row">
              <Link to={`/habits/${r.habit.id}`} className="goals__name">
                {r.habit.name}
              </Link>
              <span className="goals__bar" aria-hidden="true">
                <span style={{ width: `${Math.round((r.rate ?? 0) * 100)}%` }} />
              </span>
              <span className="goals__value num">{few ? t('stats.goals.few') : `${Math.round((r.rate ?? 0) * 100)} % · ${r.done}/${r.total}`}</span>
              {risk && <span className="badge badge--warning">{t('stats.goals.risk')}</span>}
            </li>
          );
        })}
      </ul>
      {atRisk > 0 && (
        <Link to="/chat" className="btn btn--outline btn--sm" style={{ alignSelf: 'flex-start' }}>
          {t('stats.goals.askChat')}
        </Link>
      )}
    </section>
  );
}
